/**
 * Login gate — Google, GitHub, and email magic link (personal-app auth).
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { BrandIcon, Button } from '../ui/common';

export const LoginScreen: React.FC = () => {
  const {
    error,
    signInWithGoogle,
    signInWithGithub,
    sendMagicLink,
    completeMagicLinkIfPresent,
    clearError,
  } = useAuth();
  const [email, setEmail] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      setBusy(true);
      try {
        await completeMagicLinkIfPresent();
      } finally {
        setBusy(false);
      }
    })();
  }, [completeMagicLinkIfPresent]);

  const onGoogle = async () => {
    clearError();
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch {
      // Error surfaced via AuthContext
    } finally {
      setBusy(false);
    }
  };

  const onMagicLink = async (event: React.FormEvent) => {
    event.preventDefault();
    clearError();
    setBusy(true);
    try {
      await sendMagicLink(email);
      setLinkSent(true);
    } catch {
      setLinkSent(false);
    } finally {
      setBusy(false);
    }
  };

  const onGithub = async () => {
    clearError();
    setBusy(true);
    try {
      await signInWithGithub();
    } catch {
      // Error surfaced via AuthContext
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--z-bg)',
        color: 'var(--z-text)',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          padding: '28px 24px',
          borderRadius: 16,
          background: 'var(--z-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <BrandIcon source="zunelo" size={40} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em' }}>
              Zunelo
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--z-dim)' }}>
              Sign in to open your attention workspace
            </span>
          </div>
        </div>

        <Button
          variant="primary"
          onClick={() => void onGoogle()}
          disabled={busy}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          Continue with Google
        </Button>

        <Button
          variant="ghost"
          onClick={() => void onGithub()}
          disabled={busy}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          Continue with GitHub
        </Button>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: 'var(--z-dim)',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          <span style={{ flex: 1, height: 1, background: 'var(--z-fill-2)' }} />
          or email link
          <span style={{ flex: 1, height: 1, background: 'var(--z-fill-2)' }} />
        </div>

        <form onSubmit={(e) => void onMagicLink(e)} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={busy}
            style={{
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid var(--z-fill-2)',
              background: 'var(--z-fill-1, var(--z-fill-2))',
              color: 'var(--z-text)',
              fontSize: 14,
            }}
          />
          <Button variant="ghost" type="submit" disabled={busy || !email.trim()}>
            Send magic link
          </Button>
        </form>

        {linkSent && (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--z-ok-ink)', lineHeight: 1.45 }}>
            Check your inbox for the sign-in link, then return here.
          </p>
        )}

        {error && (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--z-danger, #c44)', lineHeight: 1.45 }}>
            {error}
          </p>
        )}

        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--z-dim)', lineHeight: 1.45 }}>
          Any user with a verified email can sign in. Slack and AI secrets never leave the
          server or cross user accounts.
        </p>
      </div>
    </div>
  );
};
